import { Http } from '@/services/http'
import { InvokeThrottle } from '@/common/decorators/invoke-throttle'
import { ParamsClean } from '@/common/decorators/params-clean'
import { Injectable } from '@tanbo/vue-di-plugin'

export interface DeviceStatus {
  device_id: string // 终端id
  computer_name: string // 计算机名
  user_mark: string // 登记用户名
  cpu_percent: number // cpu百分比
  cpu_temperature: number // cpu温度，单位摄氏度
  disk_temperature: number // 磁盘温度，单位摄氏度
  memory: string // 内存总量，格式化表述字符串，例如100G
  used_memory: string // 已用内存，格式化表述字符串，例如100G
  up_speed: string // 上行速度，例如1.1k/S，10M/S，前端可直接展示
  down_speed: string // 下行速度，例如1.1k/S，10M/S，前端可直接展示
  system_disk: string // 系统盘总量，格式化表述字符串，例如100G
  used_system_disk: string // 已用系统盘大小，格式化表述字符串，例如100G
  hard_disk: string // 硬盘总量，格式化表述字符串，例如100G
  used_hard_disk: string // 已用硬盘大小，格式化表述字符串，例如100G
  up_flow: string // 上行流量，例如1.1k，10M，前端可直接展示
  down_flow: string // 下行流量，例如1.1k，10M，前端可直接展示
  login_status?: number // 登录状态
  system_type?: number // 系统类型 1-windows 2-linux
  group_id: number // 分组id
  group_name: string // 分组名
  cpu_used_percent?: number // CPU使用率
  memory_used_percent?: number // 内存使用率
  system_disk_used_percent?: number // 系统盘使用率
  hard_disk_used_percent?: number // 硬盘使用率
  is_cpu_usage_too_high: number // 是否CPU占用过高,0-否,1-是
  is_cpu_temperature_too_high: number // 是否CPU温度过高,0-否,1-是
  is_mem_usage_too_high: number // 是否内存占用过高,0-否,1-是
  is_system_disk_space_not_enough: number // 是否系统盘占用过高,0-否,1-是
  is_whole_disk_space_not_enough: number // // 是否磁盘占用过高,0-否,1-是
}

export interface DeviceStatusQueryParams {
  group_id: number
  key_word: string
  page: number
  limit: number
  sort?: string
  asc?: boolean
  /** 统计开始时间（秒级时间戳，字符串，与流量排行等口径一致时传入） */
  start_time?: string
  /** 统计结束时间（秒级时间戳，字符串） */
  end_time?: string
  login_status:number
  /** 运行内存 */
  run_memory: number
  /** 储存资源 */
  store_resources: number
  /** 网速占用 */
  internet_occupied: number
  /** 温度情况 */
  temperature: number
}

export interface DeviceFlow {
  time: string
  up: number
  down: number
}

@Injectable()
export class DeviceStatusService {
  constructor (private http: Http) {
  }

  @ParamsClean()
  @InvokeThrottle()
  getDeviceStatusList (params: Partial<DeviceStatusQueryParams>) {
    params.login_status ||= -1
    return this.http.post<{
      total: number
      list: DeviceStatus[]
    }>('/safemonitor/terminal-status/stat', params)
  }

  getDeviceFlowDetail (terminalId: string) {
    return this.http.post<{
      one_hour_speed: DeviceFlow[]
      one_weak_flow: DeviceFlow[]
    }>('/safemonitor/flow-stat/info', {
      device_id: terminalId
    })
  }

  upgrade (group_id?: number) {
    return this.http.post<{
      task_id: string
    }>('/terminal-order/client-upgrade', {
      group_id
    })
  }

  download (type: 0 | 1, options: Partial<DeviceStatusQueryParams>, moduleName: string) {
    const params = Object.assign({}, options, {
      file_type: type,
      page: 1, // 不分页
      limit: 9999999
    })
    return this.http.downloadWithBlob('/safemonitor/terminal-status/stat/export', params, moduleName)
  }
}

enum SORT_COLUMN {
  CPU_TEMPERATURE = 'cpu_temperature',
  DISK_TEMPERATURE = 'disk_temperature',
  SYSTEM_DISK_USED_PERCENT = 'system_disk_used_percent',
  HARD_DISK_USED_PERCENT = 'hard_disk_used_percent',
  UP_FLOW = 'up_flow',
  DOWN_FLOW = 'down_flow',
  CPU_USED_PERCENT = 'cpu_used_percent',
  MEMORY_USED_PERCENT = 'memory_used_percent'
}
export function computedDefaultSort (query: any = {}, options: DeviceStatusQueryParams) {
  const sortColumn = Object.keys(SORT_COLUMN).map(key => SORT_COLUMN[key])

  if (sortColumn.includes(query?.sort)) {
    const asc = query.asc === 'true'
    options.asc = asc
    options.sort = query.sort

    return {
      prop: query.sort,
      order: asc ? 'ascending' : 'descending'
    }
  }

  return {}
}
